import { useCallback } from 'react';

import {
  buildEnterpriseUrl,
  buildManagementUrl,
  getEnterpriseIdFromOrg,
  useNavigate,
  useViewedOrganization,
} from '@cast/infrastructure-organization';
import { REPORTING_ROUTE_SEGMENTS } from '@cast/shared-trial-ui';
import { Organization, OrganizationType } from '@cast/types';

/** Prefer enterprise routes for CHILD / ENTERPRISE; management for DEFAULT. */
export const getReportingPlansUrl = (org?: Organization | null): string => {
  if (!org) {
    return buildManagementUrl(REPORTING_ROUTE_SEGMENTS.plans);
  }

  if (org.type === OrganizationType.ENTERPRISE) {
    return buildEnterpriseUrl(org.id, REPORTING_ROUTE_SEGMENTS.plans);
  }

  const enterpriseId = getEnterpriseIdFromOrg(org);
  if (org.type === OrganizationType.CHILD && enterpriseId) {
    return buildEnterpriseUrl(
      enterpriseId,
      REPORTING_ROUTE_SEGMENTS.plans,
      org.id
    );
  }

  return buildManagementUrl(REPORTING_ROUTE_SEGMENTS.plans);
};

const getReportingUsageUrl = (org?: Organization | null): string => {
  if (!org) {
    return buildManagementUrl(REPORTING_ROUTE_SEGMENTS.reporting);
  }

  if (org.type === OrganizationType.ENTERPRISE) {
    return buildEnterpriseUrl(org.id, REPORTING_ROUTE_SEGMENTS.reporting);
  }

  const enterpriseId = getEnterpriseIdFromOrg(org);
  if (org.type === OrganizationType.CHILD && enterpriseId) {
    return buildEnterpriseUrl(
      enterpriseId,
      REPORTING_ROUTE_SEGMENTS.reporting,
      org.id
    );
  }

  return buildManagementUrl(REPORTING_ROUTE_SEGMENTS.reporting);
};

/** Navigate to Platform usage / Plans (reporting monetization). */
export const useNavigateToReportingPlans = () => {
  const navigate = useNavigate();
  const viewedOrganization = useViewedOrganization();
  return useCallback(
    () => navigate(getReportingPlansUrl(viewedOrganization)),
    [navigate, viewedOrganization]
  );
};

/** Navigate to Platform usage / Reporting summary. */
export const useNavigateToReportingUsage = () => {
  const navigate = useNavigate();
  const viewedOrganization = useViewedOrganization();
  return useCallback(
    () => navigate(getReportingUsageUrl(viewedOrganization)),
    [navigate, viewedOrganization]
  );
};
